"use client";

import React, { useRef, useState } from "react";
import { ArrowRight, Play, Shield, Activity, Sparkles, CheckCircle2 } from "lucide-react";
import { StatusBadge } from "./technical-components";

interface HeroSectionProps {
  onOpenDemo: () => void;
  onOpenVideo: () => void;
}

export function HeroSection({ onOpenDemo, onOpenVideo }: HeroSectionProps) {
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 border-b border-[#3A3A38]/20 overflow-hidden bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column - Headline & Specs */}
          <div className="lg:col-span-7 space-y-8">
            {/* Status Badge */}
            <StatusBadge text="SYSTEM OPERATIONAL // K-12 SPORTS ERP & BIOMETRICS" />

            {/* Main Header: Space Grotesk 8xl, Forest Green */}
            <div className="space-y-4">
              <h1 className="font-space font-extrabold text-5xl sm:text-7xl lg:text-8xl text-[#1A3C2B] tracking-tight leading-[0.9] uppercase">
                INTELLIGENT <br />
                <span className="text-[#1A3C2B] underline decoration-[#FF8C69] decoration-4 underline-offset-8">
                  SPORTS OPERATING
                </span> <br />
                SYSTEM.
              </h1>
            </div>

            {/* Subtext: JetBrains Mono, 14px, All-caps, Vertical 1px line on left */}
            <div className="border-l-2 border-[#1A3C2B] pl-4 text-xs sm:text-sm font-mono-tech uppercase tracking-wider text-[#3A3A38] leading-relaxed max-w-xl">
              RACE TO OLYMPICS — TRANSFORMING SCHOOL PLAYGROUNDS INTO HIGH-PERFORMANCE BIOMETRIC TALENT NURSERIES AND MONETIZED CAMPUS SPORTS ASSETS.
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2 font-mono-tech">
              <button
                onClick={onOpenDemo}
                className="px-6 py-3.5 bg-[#1A3C2B] text-white hover:bg-[#132d20] transition border border-[#1A3C2B] rounded-none flex items-center gap-3 text-xs uppercase tracking-wider font-bold shadow-none"
              >
                <span>BOOK ENTERPRISE DEMO</span>
                <ArrowRight className="w-4 h-4 text-[#9EFFBF]" />
              </button>

              <button
                onClick={onOpenVideo}
                className="px-6 py-3.5 bg-transparent border border-[#3A3A38]/30 text-[#1A3C2B] hover:bg-[#1A3C2B]/5 transition rounded-none flex items-center gap-2 text-xs uppercase tracking-wider font-bold"
              >
                <Play className="w-3.5 h-3.5 fill-[#1A3C2B]" />
                <span>WATCH PLATFORM FILM (3-MIN)</span>
              </button>
            </div>

            {/* Key Metric Chips */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-[#3A3A38]/20 max-w-lg font-mono-tech text-[11px]">
              <div>
                <span className="text-[#1A3C2B] font-bold block text-base font-space">100%</span>
                <span className="text-[#3A3A38]/70 uppercase">DPDP ACT 2023 COMPLIANT</span>
              </div>
              <div>
                <span className="text-[#1A3C2B] font-bold block text-base font-space">9 MODULES</span>
                <span className="text-[#3A3A38]/70 uppercase">FULL DUAL-ENGINE ERP</span>
              </div>
              <div>
                <span className="text-[#1A3C2B] font-bold block text-base font-space">MIRWALD PHV</span>
                <span className="text-[#3A3A38]/70 uppercase">BIOMETRIC TALENT RADAR</span>
              </div>
            </div>
          </div>

          {/* Right Column - Abstract Wireframe Container + Video */}
          <div className="lg:col-span-5 relative flex justify-center">
            {/* 16:9 Aspect Video Wireframe Container */}
            <div className="relative w-full max-w-[520px] border border-[#3A3A38]/30 bg-[#F7F7F5] p-3 sm:p-4 rounded-none shadow-none flex flex-col justify-between">
              {/* Corner L-Markers */}
              <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t-2 border-l-2 border-[#1A3C2B]" />
              <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t-2 border-r-2 border-[#1A3C2B]" />
              <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b-2 border-l-2 border-[#1A3C2B]" />
              <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b-2 border-r-2 border-[#1A3C2B]" />

              {/* Wireframe Header Bar */}
              <div className="border-b border-[#3A3A38]/20 pb-2 mb-2 flex items-center justify-between font-mono-tech text-[10px] uppercase text-[#1A3C2B]">
                <div className="flex items-center gap-1.5 font-bold">
                  <div className="w-2 h-2 bg-[#1A3C2B] animate-pulse" />
                  <span>DEMO_PREVIEW.MP4 // 1080P</span>
                </div>
                <span className="text-[#3A3A38]/70 font-bold">LIVE TELEMETRY</span>
              </div>

              {/* Central 16:9 Video Canvas Frame */}
              <div className="relative aspect-video w-full bg-slate-950 border border-[#3A3A38]/20 overflow-hidden group my-1">
                <video
                  ref={videoRef}
                  src="/media/final.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                />
                
                {/* Controls overlay */}
                <div className="absolute bottom-2.5 right-2.5 z-20">
                  <button
                    onClick={toggleVideo}
                    className="px-2.5 py-1 bg-[#1A3C2B] text-white border border-[#1A3C2B] rounded-none font-mono-tech text-[10px] uppercase tracking-wider flex items-center gap-1.5 hover:bg-[#132d20] shadow-sm"
                  >
                    <Play className="w-3 h-3 fill-current text-[#9EFFBF]" />
                    <span>{isPlaying ? "PAUSE" : "PLAY"}</span>
                  </button>
                </div>
              </div>

              {/* Wireframe Footer Info */}
              <div className="border-t border-[#3A3A38]/20 pt-2 mt-1 flex items-center justify-between font-mono-tech text-[10px] text-[#3A3A38]">
                <span className="font-bold text-[#1A3C2B]">STREAM: STABLE</span>
                <span className="text-[#1A3C2B] font-bold">LATENCY: 12MS</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
